import { Action } from 'redux';
import { InputCircle } from '../../../../cross-cutting-concerns/communication/interfaces/am-api-graphql';
import {
  IActionCreatorErrorOptions,
  IErrorAction,
} from '../../../../cross-cutting-concerns/state-management/interfaces/StateManagement.types';
import { SiteClientDeleteOptions } from '../../SiteClient';
import {
  SiteDelete,
  SiteAssignMachineResponse,
  SiteDetails,
  SiteUnassignMachineResponse,
  SiteUpdateResponse,
  SiteGeofenceSetupResponse,
  SiteAssignManagersResponse,
  SiteUnassignManagerResponse,
  SiteMachineVariantData,
  SiteTagsUpdateResponse,
} from '../../interfaces/Site.types';
import {
  SiteServiceAssignMachineOptions,
  SiteServiceAssignManagersOptions,
  SiteServiceDetailsOptions,
  SiteServiceSetGeofenceOptions,
  SiteServiceUnassignMachineOptions,
  SiteServiceUnassignManagerOptions,
  SiteServiceUpdateOptions,
  SiteServiceUpdateSiteTagsOptions,
} from '../../SiteService';
import { Optional } from 'lib/types/Optional';
import { CleaningActions } from 'app/modules/cleaning/state/cleaningActions';

export type IGetSiteDetailsRequestOptions = SiteServiceDetailsOptions;
export type IGetSiteDetailsRequestAction = Action<typeof SiteDetailsActions.GET_SITE_DETAILS_REQUEST> & {
  payload: SiteServiceDetailsOptions;
};

export type IGetSiteDetailsSuccessOptions = SiteDetails;
export type IGetSiteDetailsSuccessAction = Action<typeof SiteDetailsActions.GET_SITE_DETAILS_SUCCESS> & {
  payload: SiteDetails;
};

export type IGetSiteDetailsMachinePicturesSuccessOptions = SiteMachineVariantData;
export type IGetSiteDetailsMachinePicturesSuccessAction = Action<
  typeof SiteDetailsActions.GET_SITE_DETAILS_MACHINE_PICTURES_SUCCESS
> & {
  payload: SiteMachineVariantData;
};

export type IGetSiteDetailsErrorAction = IErrorAction<typeof SiteDetailsActions.GET_SITE_DETAILS_ERROR>;

export type IUpdateSiteRequestOptions = Omit<SiteServiceUpdateOptions, 'input'> & {
  input: {
    name: string;
  };
};
export type IUpdateSiteRequestAction = Action<typeof SiteDetailsActions.UPDATE_SITE_REQUEST> & {
  payload: SiteServiceUpdateOptions;
};

export type IUpdateSiteSuccessOptions = Optional<SiteUpdateResponse>;
export type IUpdateSiteSuccessAction = Action<typeof SiteDetailsActions.UPDATE_SITE_SUCCESS> & {
  payload: Optional<SiteUpdateResponse>;
};

export type IUpdateSiteErrorAction = IErrorAction<typeof SiteDetailsActions.UPDATE_SITE_ERROR>;

export type IUpdateSiteLocationRequestOptions = Omit<SiteServiceUpdateOptions, 'input'> & {
  currentSiteAddress: string;
  input: {
    location: {
      address: string;
    };
  };
};
export type IUpdateSiteLocationRequestAction = Action<typeof SiteDetailsActions.UPDATE_SITE_LOCATION_REQUEST> & {
  payload: IUpdateSiteLocationRequestOptions;
};

export type IUpdateSiteLocationSuccessOptions = Optional<SiteUpdateResponse>;
export type IUpdateSiteLocationSuccessAction = Action<typeof SiteDetailsActions.UPDATE_SITE_LOCATION_SUCCESS> & {
  payload: Optional<SiteUpdateResponse>;
};

export type IUpdateSiteLocationErrorAction = IErrorAction<typeof SiteDetailsActions.UPDATE_SITE_LOCATION_ERROR>;

export type IUpdateSiteTagsRequestOptions = SiteServiceUpdateSiteTagsOptions;
export type IUpdateSiteTagsRequestAction = Action<typeof SiteDetailsActions.UPDATE_SITE_TAGS_REQUEST> & {
  payload: SiteServiceUpdateSiteTagsOptions;
};

export type IUpdateSiteTagsSuccessOptions = Optional<SiteTagsUpdateResponse>;
export type IUpdateSiteTagsSuccessAction = Action<typeof SiteDetailsActions.UPDATE_SITE_TAGS_REQUEST> & {
  payload: Optional<SiteTagsUpdateResponse>;
};

export type IUpdateSiteTagsErrorAction = IErrorAction<typeof SiteDetailsActions.UPDATE_SITE_TAGS_ERROR>;

export type IAssignMachineToSiteRequestOptions = SiteServiceAssignMachineOptions;
export type IAssignMachineToSiteRequestAction = Action<typeof SiteDetailsActions.ASSIGN_MACHINE_TO_SITE_REQUEST> & {
  payload: SiteServiceAssignMachineOptions;
};

export type IAssignMachineToSiteSuccessOptions = Optional<SiteAssignMachineResponse>;
export type IAssignMachineToSiteSuccessAction = Action<typeof SiteDetailsActions.ASSIGN_MACHINE_TO_SITE_SUCCESS> & {
  payload: Optional<SiteAssignMachineResponse>;
};

export type IAssignMachineToSiteErrorAction = IErrorAction<typeof SiteDetailsActions.ASSIGN_MACHINE_TO_SITE_ERROR>;

export type IUnassignMachineFromSiteRequestOptions = SiteServiceUnassignMachineOptions;
export type IUnassignMachineFromSiteRequestAction = Action<
  typeof SiteDetailsActions.UNASSIGN_MACHINE_FROM_SITE_REQUEST
> & {
  payload: SiteServiceUnassignMachineOptions;
};

export type IUnassignMachineFromSiteSuccessOptions = Optional<SiteUnassignMachineResponse>;
export type IUnassignMachineFromSiteSuccessAction = Action<
  typeof SiteDetailsActions.UNASSIGN_MACHINE_FROM_SITE_SUCCESS
> & {
  payload: Optional<SiteUnassignMachineResponse>;
};

export type IUnassignMachineFromSiteErrorAction = IErrorAction<
  typeof SiteDetailsActions.UNASSIGN_MACHINE_FROM_SITE_ERROR
>;

export type IChangeSiteMachineListTablePage = Action<typeof SiteDetailsActions.CHANGE_TABLE_PAGE> & {
  payload: {
    page: number;
  };
};

export type IChangeSiteMachineListTablePageSize = Action<typeof SiteDetailsActions.CHANGE_TABLE_PAGE_SIZE> & {
  payload: {
    pageSize: number;
  };
};

export type IDeleteSiteRequestOptions = SiteClientDeleteOptions;
export type IDeleteSiteRequestAction = Action<typeof SiteDetailsActions.DELETE_SITE_REQUEST> & {
  payload: SiteClientDeleteOptions;
};

export type IDeleteSiteSuccessOptions = Optional<SiteDelete>;
export type IDeleteSiteSuccessAction = Action<typeof SiteDetailsActions.DELETE_SITE_SUCCESS> & {
  payload: Optional<SiteDelete>;
};

export type IDeleteSiteErrorAction = IErrorAction<typeof SiteDetailsActions.DELETE_SITE_ERROR>;

export type ISetGeofenceRequestOptions = SiteServiceSetGeofenceOptions;
export type ISetGeofenceRequestAction = Action<typeof SiteDetailsActions.SET_GEOFENCE_REQUEST> & {
  payload: SiteServiceSetGeofenceOptions;
};

export type ISetGeofenceSuccessOptions = Optional<SiteGeofenceSetupResponse>;
export type ISetGeofenceSuccessAction = Action<typeof SiteDetailsActions.SET_GEOFENCE_REQUEST> & {
  payload: Optional<SiteGeofenceSetupResponse>;
};

export type ISetGeofenceErrorAction = IErrorAction<typeof SiteDetailsActions.SET_GEOFENCE_ERROR>;

export type IPollSiteDetailsWithGeofenceActionOptions = SiteServiceDetailsOptions & {
  geofence: {
    circle: InputCircle;
  };
};
export type IPollSiteDetailsWithGeofenceAction = Action<typeof SiteDetailsActions.POLL_SITE_DETAILS_WITH_GEOFENCE> & {
  payload: IPollSiteDetailsWithGeofenceActionOptions;
};

export type ISiteDetailsMapIsLoadingAction = Action<typeof SiteDetailsActions.MAP_IS_LOADING>;
export type ISiteDetailsMapIsNotLoadingAction = Action<typeof SiteDetailsActions.MAP_IS_NOT_LOADING>;

export interface ISetGeofenceAfterSiteLocationIsInitializedOptions {
  siteId: string;
  geofence: {
    circle: InputCircle;
  };
}
export type ISetGeofenceAfterSiteLocationIsInitializedAction = Action<
  typeof SiteDetailsActions.SET_GEOFENCE_AFTER_SITE_LOCATION_IS_INITIALIZED
> & {
  payload: ISetGeofenceAfterSiteLocationIsInitializedOptions;
};

// ASSIGN_MANAGERS

export type IAssignManagersToSiteRequestOptions = SiteServiceAssignManagersOptions;
export type IAssignManagersToSiteRequestAction = Action<typeof SiteDetailsActions.ASSIGN_MANAGERS_TO_SITE_REQUEST> & {
  payload: SiteServiceAssignManagersOptions;
};

export type IAssignManagersToSiteSuccessOptions = Optional<SiteAssignManagersResponse>;
export type IAssignManagersToSiteSuccessAction = Action<typeof SiteDetailsActions.ASSIGN_MANAGERS_TO_SITE_SUCCESS> & {
  payload: Optional<SiteAssignManagersResponse>;
};

export type IAssignManagersToSiteErrorAction = IErrorAction<typeof SiteDetailsActions.ASSIGN_MANAGERS_TO_SITE_ERROR>;

// UNASSIGN_MANAGER

export type IUnassignManagerFromSiteRequestOptions = SiteServiceUnassignManagerOptions;
export type IUnassignManagerFromSiteRequestAction = Action<
  typeof SiteDetailsActions.UNASSIGN_MANAGER_FROM_SITE_REQUEST
> & {
  payload: SiteServiceUnassignManagerOptions;
};

export type IUnassignManagerFromSiteSuccessOptions = Optional<SiteUnassignManagerResponse>;
export type IUnassignManagerFromSiteSuccessAction = Action<
  typeof SiteDetailsActions.UNASSIGN_MANAGER_FROM_SITE_SUCCESS
> & {
  payload: Optional<SiteUnassignManagerResponse>;
};

export type IUnassignManagerFromSiteErrorAction = IErrorAction<
  typeof SiteDetailsActions.UNASSIGN_MANAGER_FROM_SITE_ERROR
>;

export class SiteDetailsActions {
  public static GET_SITE_DETAILS_REQUEST = 'SITE_DETAILS::GET_SITE_DETAILS_REQUEST';
  public static GET_SITE_DETAILS_SUCCESS = 'SITE_DETAILS::GET_SITE_DETAILS_SUCCESS';
  public static GET_SITE_DETAILS_MACHINE_PICTURES_SUCCESS = 'SITE_DETAILS::GET_SITE_DETAILS_MACHINE_PICTURES_SUCCESS';
  public static GET_SITE_DETAILS_ERROR = 'SITE_DETAILS::GET_SITE_DETAILS_ERROR';

  public static GET_SITE_CLEANING_PLAN_REQUEST = 'SITE_DETAILS::GET_SITE_CLEANING_PLAN_REQUEST';
  public static GET_SITE_CLEANING_PLAN_SUCCESS = 'SITE_DETAILS::GET_SITE_CLEANING_PLAN_SUCCESS';
  public static GET_SITE_CLEANING_PLAN_ERROR = 'SITE_DETAILS::GET_SITE_CLEANING_PLAN_ERROR';

  public static UPDATE_SITE_REQUEST = 'SITE_DETAILS::UPDATE_SITE_REQUEST';
  public static UPDATE_SITE_SUCCESS = 'SITE_DETAILS::UPDATE_SITE_SUCCESS';
  public static UPDATE_SITE_ERROR = 'SITE_DETAILS::UPDATE_SITE_ERROR';

  public static UPDATE_SITE_LOCATION_REQUEST = 'SITE_DETAILS::UPDATE_SITE_LOCATION_REQUEST';
  public static UPDATE_SITE_LOCATION_SUCCESS = 'SITE_DETAILS::UPDATE_SITE_LOCATION_SUCCESS';
  public static UPDATE_SITE_LOCATION_ERROR = 'SITE_DETAILS::UPDATE_SITE_LOCATION_ERROR';

  public static UPDATE_SITE_TAGS_REQUEST = 'SITE_DETAILS::UPDATE_SITE_TAGS_REQUEST';
  public static UPDATE_SITE_TAGS_SUCCESS = 'SITE_DETAILS::UPDATE_SITE_TAGS_SUCCESS';
  public static UPDATE_SITE_TAGS_ERROR = 'SITE_DETAILS::UPDATE_SITE_TAGS_ERROR';

  public static ASSIGN_MACHINE_TO_SITE_REQUEST = 'SITE_DETAILS::ASSIGN_MACHINE_TO_SITE_REQUEST';
  public static ASSIGN_MACHINE_TO_SITE_SUCCESS = 'SITE_DETAILS::ASSIGN_MACHINE_TO_SITE_SUCCESS';
  public static ASSIGN_MACHINE_TO_SITE_ERROR = 'SITE_DETAILS::ASSIGN_MACHINE_TO_SITE_ERROR';

  public static UNASSIGN_MACHINE_FROM_SITE_REQUEST = 'SITE_DETAILS::UNASSIGN_MACHINE_FROM_SITE_REQUEST';
  public static UNASSIGN_MACHINE_FROM_SITE_SUCCESS = 'SITE_DETAILS::UNASSIGN_MACHINE_FROM_SITE_SUCCESS';
  public static UNASSIGN_MACHINE_FROM_SITE_ERROR = 'SITE_DETAILS::UNASSIGN_MACHINE_FROM_SITE_ERROR';

  public static CHANGE_TABLE_PAGE = 'SITE_DETAILS::CHANGE_TABLE_PAGE';
  public static CHANGE_TABLE_PAGE_SIZE = 'SITE_DETAILS::CHANGE_TABLE_PAGE_SIZE';
  public static RESET_STATE = 'SITE_DETAILS::RESET_STATE';

  public static DELETE_SITE_REQUEST = 'SITE_DETAILS::DELETE_SITE_REQUEST';
  public static DELETE_SITE_SUCCESS = 'SITE_DETAILS::DELETE_SITE_SUCCESS';
  public static DELETE_SITE_ERROR = 'SITE_DETAILS::DELETE_SITE_ERROR';

  public static SET_GEOFENCE_REQUEST = 'SITE_DETAILS::SET_GEOFENCE_REQUEST';
  public static SET_GEOFENCE_SUCCESS = 'SITE_DETAILS::SET_GEOFENCE_SUCCESS';
  public static SET_GEOFENCE_ERROR = 'SITE_DETAILS::SET_GEOFENCE_ERROR';

  public static ASSIGN_MANAGERS_TO_SITE_REQUEST = 'SITE_DETAILS::ASSIGN_MANAGERS_TO_SITE_REQUEST';
  public static ASSIGN_MANAGERS_TO_SITE_SUCCESS = 'SITE_DETAILS::ASSIGN_MANAGERS_TO_SITE_SUCCESS';
  public static ASSIGN_MANAGERS_TO_SITE_ERROR = 'SITE_DETAILS::ASSIGN_MANAGERS_TO_SITE_ERROR';

  public static UNASSIGN_MANAGER_FROM_SITE_REQUEST = 'SITE_DETAILS::UNASSIGN_MANAGER_FROM_SITE_REQUEST';
  public static UNASSIGN_MANAGER_FROM_SITE_SUCCESS = 'SITE_DETAILS::UNASSIGN_MANAGER_FROM_SITE_SUCCESS';
  public static UNASSIGN_MANAGER_FROM_SITE_ERROR = 'SITE_DETAILS::UNASSIGN_MANAGER_FROM_SITE_ERROR';

  public static SET_GEOFENCE_AFTER_SITE_LOCATION_IS_INITIALIZED =
    'SITE_DETAILS::SET_GEOFENCE_AFTER_SITE_LOCATION_IS_INITIALIZED';

  public static POLL_SITE_DETAILS_WITH_GEOFENCE = 'SITE_DETAILS::START_POLL_SITE_DETAILS_WITH_GEOFENCE_REQUEST';

  public static MAP_IS_LOADING = 'SITE_DETAILS::MAP_IS_LOADING';
  public static MAP_IS_NOT_LOADING = 'SITE_DETAILS::SET_MAP_IS_NOT_LOADING';

  public static RESET_CREATE_WORK_INTERVAL_ERROR = 'SITE_DETAILS::RESET_CREATE_WORK_INTERVAL_ERROR';
  public static RESET_UPDATE_WORK_INTERVAL_ERROR = 'SITE_DETAILS::RESET_UPDATE_WORK_INTERVAL_ERROR';

  public static getSiteDetailsRequest({
    id,
    machinePaginationOptions,
  }: IGetSiteDetailsRequestOptions): IGetSiteDetailsRequestAction {
    return {
      type: SiteDetailsActions.GET_SITE_DETAILS_REQUEST,
      payload: {
        id,
        machinePaginationOptions,
      },
    };
  }

  public static getSiteDetailsSuccess(data: IGetSiteDetailsSuccessOptions): IGetSiteDetailsSuccessAction {
    return {
      type: SiteDetailsActions.GET_SITE_DETAILS_SUCCESS,
      payload: data,
    };
  }

  public static getSiteDetailsMachinePicturesSuccess(
    data: IGetSiteDetailsMachinePicturesSuccessOptions
  ): IGetSiteDetailsMachinePicturesSuccessAction {
    return {
      type: SiteDetailsActions.GET_SITE_DETAILS_MACHINE_PICTURES_SUCCESS,
      payload: data,
    };
  }

  public static getSiteDetailsError({ error }: IActionCreatorErrorOptions): IGetSiteDetailsErrorAction {
    return {
      type: SiteDetailsActions.GET_SITE_DETAILS_ERROR,
      error,
    };
  }

  public static updateSiteRequest({ id, input }: IUpdateSiteRequestOptions): IUpdateSiteRequestAction {
    return {
      type: SiteDetailsActions.UPDATE_SITE_REQUEST,
      payload: {
        id,
        input,
      },
    };
  }

  public static updateSiteSuccess(data: IUpdateSiteSuccessOptions): IUpdateSiteSuccessAction {
    return {
      type: SiteDetailsActions.UPDATE_SITE_SUCCESS,
      payload: data,
    };
  }

  public static updateSiteError({ error }: IActionCreatorErrorOptions): IUpdateSiteErrorAction {
    return {
      type: SiteDetailsActions.UPDATE_SITE_ERROR,
      error,
    };
  }

  public static updateSiteLocationRequest({
    id,
    input,
    currentSiteAddress,
  }: IUpdateSiteLocationRequestOptions): IUpdateSiteLocationRequestAction {
    return {
      type: SiteDetailsActions.UPDATE_SITE_LOCATION_REQUEST,
      payload: {
        id,
        input,
        currentSiteAddress,
      },
    };
  }

  public static updateSiteLocationSuccess(data: IUpdateSiteLocationSuccessOptions): IUpdateSiteLocationSuccessAction {
    return {
      type: SiteDetailsActions.UPDATE_SITE_LOCATION_SUCCESS,
      payload: data,
    };
  }

  public static updateSiteLocationError({ error }: IActionCreatorErrorOptions): IUpdateSiteLocationErrorAction {
    return {
      type: SiteDetailsActions.UPDATE_SITE_LOCATION_ERROR,
      error,
    };
  }

  public static updateSiteTagsRequest({ id, input }: IUpdateSiteTagsRequestOptions): IUpdateSiteTagsRequestAction {
    return {
      type: SiteDetailsActions.UPDATE_SITE_TAGS_REQUEST,
      payload: {
        id,
        input,
      },
    };
  }

  public static updateSiteTagsSuccess(data: IUpdateSiteTagsSuccessOptions): IUpdateSiteTagsSuccessAction {
    return {
      type: SiteDetailsActions.UPDATE_SITE_TAGS_SUCCESS,
      payload: data,
    };
  }

  public static updateSiteTagsError({ error }: IActionCreatorErrorOptions): IUpdateSiteTagsErrorAction {
    return {
      type: SiteDetailsActions.UPDATE_SITE_TAGS_ERROR,
      error,
    };
  }

  public static assignMachineToSiteRequest({
    siteId,
    machineIds,
  }: IAssignMachineToSiteRequestOptions): IAssignMachineToSiteRequestAction {
    return {
      type: SiteDetailsActions.ASSIGN_MACHINE_TO_SITE_REQUEST,
      payload: {
        siteId,
        machineIds,
      },
    };
  }

  public static assignMachineToSiteSuccess(
    data: IAssignMachineToSiteSuccessOptions
  ): IAssignMachineToSiteSuccessAction {
    return {
      type: SiteDetailsActions.ASSIGN_MACHINE_TO_SITE_SUCCESS,
      payload: data,
    };
  }

  public static assignMachineToSiteError({ error }: IActionCreatorErrorOptions): IAssignMachineToSiteErrorAction {
    return {
      type: SiteDetailsActions.ASSIGN_MACHINE_TO_SITE_ERROR,
      error,
    };
  }

  public static unassignMachineFromSiteRequest({
    siteId,
    machineId,
  }: IUnassignMachineFromSiteRequestOptions): IUnassignMachineFromSiteRequestAction {
    return {
      type: SiteDetailsActions.UNASSIGN_MACHINE_FROM_SITE_REQUEST,
      payload: {
        siteId,
        machineId,
      },
    };
  }

  public static unassignMachineFromSiteSuccess(
    data: IUnassignMachineFromSiteSuccessOptions
  ): IUnassignMachineFromSiteSuccessAction {
    return {
      type: SiteDetailsActions.UNASSIGN_MACHINE_FROM_SITE_SUCCESS,
      payload: data,
    };
  }

  public static unassignMachineFromSiteError({
    error,
  }: IActionCreatorErrorOptions): IUnassignMachineFromSiteErrorAction {
    return {
      type: SiteDetailsActions.UNASSIGN_MACHINE_FROM_SITE_ERROR,
      error,
    };
  }

  public static changeTablePage(data: { page: number }): IChangeSiteMachineListTablePage {
    return {
      type: SiteDetailsActions.CHANGE_TABLE_PAGE,
      payload: data,
    };
  }

  public static changeTablePageSize(data: { pageSize: number }): IChangeSiteMachineListTablePageSize {
    return {
      type: SiteDetailsActions.CHANGE_TABLE_PAGE_SIZE,
      payload: data,
    };
  }

  public static resetState(): Action<typeof SiteDetailsActions.RESET_STATE> {
    return {
      type: SiteDetailsActions.RESET_STATE,
    };
  }

  public static deleteSiteRequest({ id }: IDeleteSiteRequestOptions): IDeleteSiteRequestAction {
    return {
      type: SiteDetailsActions.DELETE_SITE_REQUEST,
      payload: {
        id,
      },
    };
  }

  public static deleteSiteSuccess(data: IDeleteSiteSuccessOptions): IDeleteSiteSuccessAction {
    return {
      type: SiteDetailsActions.DELETE_SITE_SUCCESS,
      payload: data,
    };
  }

  public static deleteSiteError({ error }: IActionCreatorErrorOptions): IDeleteSiteErrorAction {
    return {
      type: SiteDetailsActions.DELETE_SITE_ERROR,
      error,
    };
  }

  public static setGeofenceRequest({ siteId, geofence }: ISetGeofenceRequestOptions): ISetGeofenceRequestAction {
    return {
      type: SiteDetailsActions.SET_GEOFENCE_REQUEST,
      payload: {
        siteId,
        geofence,
      },
    };
  }

  public static setGeofenceSuccess(data: ISetGeofenceSuccessOptions): ISetGeofenceSuccessAction {
    return {
      type: SiteDetailsActions.SET_GEOFENCE_SUCCESS,
      payload: data,
    };
  }

  public static setGeofenceError({ error }: IActionCreatorErrorOptions): ISetGeofenceErrorAction {
    return {
      type: SiteDetailsActions.SET_GEOFENCE_ERROR,
      error,
    };
  }

  public static pollSiteDetailsWithGeofence({
    id,
    machinePaginationOptions,
    geofence,
  }: IPollSiteDetailsWithGeofenceActionOptions): IPollSiteDetailsWithGeofenceAction {
    return {
      type: SiteDetailsActions.POLL_SITE_DETAILS_WITH_GEOFENCE,
      payload: {
        id,
        machinePaginationOptions,
        geofence,
      },
    };
  }

  public static mapIsLoading(): ISiteDetailsMapIsLoadingAction {
    return {
      type: SiteDetailsActions.MAP_IS_LOADING,
    };
  }

  public static mapIsNotLoading(): ISiteDetailsMapIsNotLoadingAction {
    return {
      type: SiteDetailsActions.MAP_IS_NOT_LOADING,
    };
  }

  public static setGeofenceAfterSiteLocationIsInitialized({
    siteId,
    geofence,
  }: ISetGeofenceAfterSiteLocationIsInitializedOptions): ISetGeofenceAfterSiteLocationIsInitializedAction {
    return {
      type: SiteDetailsActions.SET_GEOFENCE_AFTER_SITE_LOCATION_IS_INITIALIZED,
      payload: {
        siteId,
        geofence,
      },
    };
  }

  public static assignManagersToSiteRequest({
    siteId,
    userIds,
  }: IAssignManagersToSiteRequestOptions): IAssignManagersToSiteRequestAction {
    return {
      type: SiteDetailsActions.ASSIGN_MANAGERS_TO_SITE_REQUEST,
      payload: {
        siteId,
        userIds,
      },
    };
  }

  public static assignManagersToSiteSuccess(
    data: IAssignManagersToSiteSuccessOptions
  ): IAssignManagersToSiteSuccessAction {
    return {
      type: SiteDetailsActions.ASSIGN_MANAGERS_TO_SITE_SUCCESS,
      payload: data,
    };
  }

  public static assignManagersToSiteError({ error }: IActionCreatorErrorOptions): IAssignManagersToSiteErrorAction {
    return {
      type: SiteDetailsActions.ASSIGN_MANAGERS_TO_SITE_ERROR,
      error,
    };
  }

  public static unassignManagerFromSiteRequest({
    siteId,
    userId,
  }: IUnassignManagerFromSiteRequestOptions): IUnassignManagerFromSiteRequestAction {
    return {
      type: SiteDetailsActions.UNASSIGN_MANAGER_FROM_SITE_REQUEST,
      payload: {
        siteId,
        userId,
      },
    };
  }

  public static unassignManagerFromSiteSuccess(
    data: IUnassignManagerFromSiteSuccessOptions
  ): IUnassignManagerFromSiteSuccessAction {
    return {
      type: SiteDetailsActions.UNASSIGN_MANAGER_FROM_SITE_SUCCESS,
      payload: data,
    };
  }

  public static unassignManagerFromSiteError({
    error,
  }: IActionCreatorErrorOptions): IUnassignManagerFromSiteErrorAction {
    return {
      type: SiteDetailsActions.UNASSIGN_MANAGER_FROM_SITE_ERROR,
      error,
    };
  }

  public static resetCreateWorkIntervalError(): Action<typeof CleaningActions.RESET_CREATE_WORK_INTERVAL_ERROR> {
    return {
      type: CleaningActions.RESET_CREATE_WORK_INTERVAL_ERROR,
    };
  }

  public static resetUpdateWorkIntervalError(): Action<typeof CleaningActions.RESET_UPDATE_WORK_INTERVAL_ERROR> {
    return {
      type: CleaningActions.RESET_UPDATE_WORK_INTERVAL_ERROR,
    };
  }
}
